import {MantineTheme} from '@mantine/core';
import {useMemo} from 'react';
import {DefaultEdgeOptions} from 'reactflow';

import BenchlingConfig from 'BenchlingConfig';
import {getExecutionDefaultEdgeOptions} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/edges/edgeUtils';
import {
  FlowchartFeatures,
  isFlowchartFeatureEnabled,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/FlowchartFeaturesContext';
import {ProcessFlowchartEditorModeFeatureType} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';

import {FlowchartEdge, ProcessFlowchartEdgeType} from './processFlowchartConfigEditorConstants';
import {ApplyFlowchartChangesFn} from './useFlowchartState/FlowchartStateTypes';

interface UseFlowchartEdgesParams {
  interactiveEdges: Array<FlowchartEdge>;
  flowchartFeatures: FlowchartFeatures;
  isExecutionViewer: boolean;
  theme: MantineTheme;
  applyFlowchartChanges: ApplyFlowchartChangesFn;
}

interface UseFlowchartEdgesReturn {
  dataFlowEdgesExceedLimit: boolean;
  hideDataFlowEdges: boolean;
  edgesForLayout: Array<FlowchartEdge>;
  edgesContext: {
    edges: Array<FlowchartEdge>;
    onEdgeRemove: (edgeId: string) => void;
  };
  defaultEdgeOptions: DefaultEdgeOptions | undefined;
}

/**
 * Computes edge-related configuration for the flowchart: default edge options, edge type mappings,
 * data-flow edge limit validation, and handlers for edge deletion and reconnection.
 */
export default function useFlowchartEdges({
  interactiveEdges,
  flowchartFeatures,
  isExecutionViewer,
  theme,
  applyFlowchartChanges,
}: UseFlowchartEdgesParams): UseFlowchartEdgesReturn {
  const dataFlowEdgesExceedLimit = useMemo(
    () =>
      BenchlingConfig.WORKFLOWS_MAX_DATA_FLOW_EDGES_VISIBLE_IN_UI <
      interactiveEdges.filter((edge) => edge.type === ProcessFlowchartEdgeType.DATA_FLOW).length,
    [interactiveEdges]
  );

  const hideDataFlowEdges =
    !isFlowchartFeatureEnabled(
      flowchartFeatures,
      ProcessFlowchartEditorModeFeatureType.SHOW_NODE_SECTIONS_AND_DATA_FLOW_EDGES
    ) || dataFlowEdgesExceedLimit;

  const edgesForLayout = useMemo(
    () =>
      hideDataFlowEdges
        ? interactiveEdges.filter((edge) => edge.type !== ProcessFlowchartEdgeType.DATA_FLOW)
        : interactiveEdges,
    [interactiveEdges, hideDataFlowEdges]
  );

  const edgesContext = useMemo(
    () => ({
      edges: interactiveEdges,
      onEdgeRemove: (edgeId: string) => {
        applyFlowchartChanges(() => ({
          nodeChanges: [],
          edgeChanges: [{id: edgeId, type: 'remove'}],
        }));
      },
    }),
    [interactiveEdges, applyFlowchartChanges]
  );

  const defaultEdgeOptions = useMemo(() => {
    if (isExecutionViewer) {
      return getExecutionDefaultEdgeOptions(theme);
    }
    return undefined;
  }, [isExecutionViewer, theme]);

  return {
    dataFlowEdgesExceedLimit,
    hideDataFlowEdges,
    edgesForLayout,
    edgesContext,
    defaultEdgeOptions,
  };
}
